import React from 'react';
import { BaseModal } from '../../../../components/Modal/BaseModal';
import { HiChatBubbleLeft, HiUser } from 'react-icons/hi2';
import { UserAvatar } from '../../../../components/UserAvatar';

export interface CommentViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  comment: string;
  user: {
    id: number;
    name: string;
    email: string;
    funcao_usuario: string;
    cargo_usuario: string;
    avatar?: string;
  };
  timestamp: string;
  action: string;
}

export const CommentViewModal: React.FC<CommentViewModalProps> = ({
  isOpen,
  onClose,
  comment,
  user,
  timestamp,
  action
}) => {
  // Função para renderizar HTML seguro dos comentários
  const renderCommentHTML = (comment: string) => {
    // Lista de tags HTML permitidas para comentários
    const allowedTags = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre'];
    
    // Função para sanitizar HTML mantendo apenas tags seguras
    const sanitizeHTML = (html: string): string => {
      // Remove scripts e outros elementos perigosos
      let sanitized = html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
        .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
        .replace(/<link\b[^<]*>/gi, '')
        .replace(/<meta\b[^<]*>/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
      
      // Remove atributos perigosos de todas as tags
      sanitized = sanitized.replace(/<(\w+)[^>]*>/g, (_, tagName) => {
        if (allowedTags.includes(tagName.toLowerCase())) {
          return `<${tagName}>`;
        }
        return '';
      });
      
      return sanitized;
    };

    return { __html: sanitizeHTML(comment) };
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Comentário"
      size="lg"
      className="max-w-3xl"
    >
      <div className="p-6 space-y-6">
        {/* Header com informações do usuário e ação */}
        <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border-l-4 border-blue-300 dark:border-blue-500">
          <UserAvatar 
            user={user} 
            size="md" 
            showName={false}
            showPopup={true}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {user.name}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {action}
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <HiUser className="w-4 h-4" />
              <span>{user.funcao_usuario} - {user.cargo_usuario}</span>
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {formatTimestamp(timestamp)}
            </div>
          </div>
        </div>

        {/* Comentário */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <HiChatBubbleLeft className="w-5 h-5 text-blue-500 dark:text-blue-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Comentário
            </h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <div 
              className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-code:text-gray-800 dark:prose-code:text-gray-200 prose-code:bg-gray-100 dark:prose-code:bg-gray-700 prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-blockquote:border-gray-300 dark:prose-blockquote:border-gray-600 prose-blockquote:text-gray-700 dark:prose-blockquote:text-gray-300"
              dangerouslySetInnerHTML={renderCommentHTML(comment)}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
};
